import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import {
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Circle,
  Clock,
  BookOpen,
  Rocket,
  Phone,
  Shield,
  Target,
  Presentation,
  BarChart3,
  Search,
  Calendar,
  Brain,
  DollarSign,
  Globe,
  TrendingUp,
  Play,
  Heart,
  HeartPulse,
  Building2,
  Scale,
  Radio,
  Layers,
  Flag,
  Smartphone,
  Table2,
  Users,
  ArrowLeft,
  GraduationCap,
  X,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  rocket: Rocket, phone: Phone, shield: Shield, target: Target,
  presentation: Presentation, chart: BarChart3, search: Search,
  calendar: Calendar, brain: Brain, metrics: TrendingUp,
  funnel: TrendingUp, dollar: DollarSign, globe: Globe,
  heart: Heart, heartpulse: HeartPulse, building: Building2,
  scale: Scale, broadcast: Radio, layers: Layers, flag: Flag,
  smartphone: Smartphone, table: Table2, users: Users,
};

const COURSE_THEME = {
  blue: { bg: 'from-[#1B365D] to-[#0071E3]', card: 'border-blue-200 bg-blue-50/40', badge: 'bg-blue-100 text-blue-700', accent: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-50' },
  rose: { bg: 'from-[#9F1239] to-[#E11D48]', card: 'border-rose-200 bg-rose-50/40', badge: 'bg-rose-100 text-rose-700', accent: 'bg-rose-500', text: 'text-rose-700', light: 'bg-rose-50' },
  cyan: { bg: 'from-[#155E75] to-[#06B6D4]', card: 'border-cyan-200 bg-cyan-50/40', badge: 'bg-cyan-100 text-cyan-700', accent: 'bg-cyan-500', text: 'text-cyan-700', light: 'bg-cyan-50' },
  emerald: { bg: 'from-[#064E3B] to-[#10B981]', card: 'border-emerald-200 bg-emerald-50/40', badge: 'bg-emerald-100 text-emerald-700', accent: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50' },
  amber: { bg: 'from-[#78350F] to-[#F59E0B]', card: 'border-amber-200 bg-amber-50/40', badge: 'bg-amber-100 text-amber-700', accent: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-50' },
};

const CHAPTER_COLORS = [
  { accent: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-100' },
  { accent: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-100' },
  { accent: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-100' },
  { accent: 'bg-purple-500', text: 'text-purple-700', light: 'bg-purple-100' },
  { accent: 'bg-rose-500', text: 'text-rose-700', light: 'bg-rose-100' },
  { accent: 'bg-cyan-500', text: 'text-cyan-700', light: 'bg-cyan-100' },
  { accent: 'bg-indigo-500', text: 'text-indigo-700', light: 'bg-indigo-100' },
  { accent: 'bg-orange-500', text: 'text-orange-700', light: 'bg-orange-100' },
  { accent: 'bg-teal-500', text: 'text-teal-700', light: 'bg-teal-100' },
  { accent: 'bg-pink-500', text: 'text-pink-700', light: 'bg-pink-100' },
  { accent: 'bg-lime-500', text: 'text-lime-700', light: 'bg-lime-100' },
  { accent: 'bg-sky-500', text: 'text-sky-700', light: 'bg-sky-100' },
  { accent: 'bg-violet-500', text: 'text-violet-700', light: 'bg-violet-100' },
];

const mdComponents = {
  h3: ({ children }) => <h3 className="text-base sm:text-lg font-bold text-[#1B365D] mt-6 mb-3 pb-2 border-b border-slate-100">{children}</h3>,
  p: ({ children }) => <p className="text-sm sm:text-[15px] leading-relaxed text-slate-700 mb-4">{children}</p>,
  ul: ({ children }) => <ul className="space-y-2 mb-5 ml-1">{children}</ul>,
  ol: ({ children }) => <ol className="space-y-2 mb-5 ml-1 list-decimal list-inside">{children}</ol>,
  li: ({ children }) => (
    <li className="text-sm sm:text-[15px] leading-relaxed text-slate-700 flex items-start gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-[#0071E3] mt-2.5 flex-shrink-0" />
      <span className="flex-1">{children}</span>
    </li>
  ),
  strong: ({ children }) => <strong className="font-semibold text-[#1B365D]">{children}</strong>,
  em: ({ children }) => <em className="text-slate-500 not-italic text-xs sm:text-sm">{children}</em>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#0071E3] hover:underline font-medium break-words">
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-[3px] border-[#0071E3] bg-[#0071E3]/5 px-3 sm:px-5 py-3 sm:py-4 my-5 rounded-r-xl text-slate-700 italic text-sm sm:text-base">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-5 rounded-xl border border-slate-200 shadow-sm -mx-2 sm:mx-0">
      <table className="w-full text-xs sm:text-sm min-w-[400px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#1B365D] text-white">{children}</thead>,
  th: ({ children }) => <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-slate-700 border-b border-slate-50">{children}</td>,
  tr: ({ children }) => <tr className="hover:bg-slate-50/80 transition-colors even:bg-slate-50/40">{children}</tr>,
  hr: () => <hr className="my-6 border-slate-200" />,
};


// ─── Search Results Panel ───
const SearchResults = ({ results, onSelect, onClose, query }) => {
  if (!query || query.length < 2) return null;

  return (
    <div className="mt-3" data-testid="search-results">
      {results.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center text-sm text-slate-400">
            No results found for "{query}"
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
          <p className="text-xs text-slate-400 font-medium px-1">{results.length} result{results.length !== 1 ? 's' : ''} found</p>
          {results.map((r, idx) => {
            const theme = COURSE_THEME[r.courseColor] || COURSE_THEME.blue;
            return (
              <Card
                key={`${r.courseId}-${r.chapterId}-${r.sectionIndex}-${idx}`}
                className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer"
                onClick={() => onSelect(r)}
                data-testid={`search-result-${idx}`}
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${theme.light}`}>
                      <BookOpen className={`w-4 h-4 ${theme.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1">
                        <Badge className={`${theme.badge} border-0 text-[9px] sm:text-[10px] py-0`}>{r.courseTitle}</Badge>
                        <span className="text-[9px] sm:text-[10px] text-slate-400">Ch. {r.chapterOrder}</span>
                      </div>
                      <h4 className="font-semibold text-[#1B365D] text-sm leading-snug truncate">{r.chapterTitle}</h4>
                      <p className="text-xs text-slate-500 truncate">{r.sectionTitle}</p>
                      {r.snippet && (
                        <p className="text-xs text-slate-400 mt-1 line-clamp-1" dangerouslySetInnerHTML={{ __html: r.snippet }} />
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── Chapter Reader (fullscreen) ───
const ChapterReader = ({ chapter, chapterIndex, onClose, onComplete, completedSections, onSectionComplete }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [showMobileSections, setShowMobileSections] = useState(false);
  const sections = chapter.sections || [];
  const totalSections = sections.length;
  const colors = CHAPTER_COLORS[chapterIndex % CHAPTER_COLORS.length];
  const sectionProgress = ((currentSection + 1) / totalSections) * 100;

  const handleNext = () => {
    const sectionKey = `${chapter.id}-${currentSection}`;
    onSectionComplete(sectionKey);
    if (currentSection < totalSections - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo(0, 0);
    } else {
      onComplete(chapter.id);
    }
  };

  const goToSection = (idx) => {
    setCurrentSection(idx);
    setShowMobileSections(false);
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col" data-testid="chapter-reader">
      {/* Header */}
      <div className="border-b border-slate-200 px-3 sm:px-6 py-2.5 sm:py-3 bg-white shadow-sm">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-600 hover:text-slate-900 gap-1 sm:gap-2 px-2 sm:px-3 flex-shrink-0" data-testid="reader-back-btn">
              <ChevronLeft className="w-4 h-4" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div className="h-5 w-px bg-slate-200 hidden sm:block" />
            <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-lg ${colors.light} flex items-center justify-center flex-shrink-0`}>
              <span className={`text-[10px] sm:text-xs font-bold ${colors.text}`}>{chapter.order}</span>
            </div>
            <h1 className="font-semibold text-[#1B365D] text-xs sm:text-sm md:text-base truncate">{chapter.title}</h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setShowMobileSections(!showMobileSections)} className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="mobile-sections-toggle">
              <BookOpen className="w-4 h-4" />
            </button>
            <Badge className={`${colors.light} ${colors.text} border-0 hidden sm:flex`}>
              <Clock className="w-3 h-3 mr-1" />{chapter.estimated_time}
            </Badge>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="px-3 sm:px-6 py-2 sm:py-2.5 bg-slate-50/80 border-b border-slate-200">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] sm:text-xs text-slate-500 font-medium truncate mr-2">
              {currentSection + 1}/{totalSections}: {sections[currentSection]?.title}
            </span>
            <span className="text-[10px] sm:text-xs font-semibold text-[#1B365D] flex-shrink-0">{Math.round(sectionProgress)}%</span>
          </div>
          <Progress value={sectionProgress} className="h-1 sm:h-1.5" />
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile sections drawer */}
        <AnimatePresence>
          {showMobileSections && (
            <motion.div
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute inset-y-0 left-0 w-64 sm:w-72 bg-white border-r border-slate-200 z-10 shadow-xl lg:hidden overflow-y-auto p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-2">Sections</h3>
                <button onClick={() => setShowMobileSections(false)} className="p-1 rounded hover:bg-slate-100">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
              {sections.map((section, idx) => {
                const sectionKey = `${chapter.id}-${idx}`;
                const isDone = completedSections.includes(sectionKey);
                return (
                  <button key={idx} onClick={() => goToSection(idx)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-[13px] transition-all mb-0.5 ${
                      idx === currentSection ? 'bg-slate-100 text-[#1B365D] font-medium border border-slate-200'
                        : isDone ? 'text-emerald-600 hover:bg-slate-50'
                        : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {isDone ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      : idx === currentSection ? <div className="w-3.5 h-3.5 rounded-full border-2 border-[#1B365D] flex-shrink-0" />
                      : <Circle className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />}
                    <span className="truncate">{section.title}</span>
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop sidebar */}
        <div className="w-72 bg-slate-50/50 border-r border-slate-200 overflow-y-auto p-4 hidden lg:block">
          <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 px-2">Sections</h3>
          <div className="space-y-0.5">
            {sections.map((section, idx) => {
              const sectionKey = `${chapter.id}-${idx}`;
              const isDone = completedSections.includes(sectionKey);
              return (
                <button key={idx} onClick={() => setCurrentSection(idx)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-[13px] transition-all ${
                    idx === currentSection ? 'bg-white text-[#1B365D] font-medium shadow-sm border border-slate-200'
                      : isDone ? 'text-emerald-600 hover:bg-white/80'
                      : 'text-slate-500 hover:bg-white/80 hover:text-slate-700'
                  }`}
                  data-testid={`section-nav-${idx}`}
                >
                  {isDone ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    : idx === currentSection ? <div className="w-3.5 h-3.5 rounded-full border-2 border-[#1B365D] flex-shrink-0" />
                    : <Circle className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />}
                  <span className="truncate">{section.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto" onClick={() => showMobileSections && setShowMobileSections(false)}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-8">
            <AnimatePresence mode="wait">
              <motion.div key={currentSection} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>
                <div className="mb-6">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${colors.text}`}>Section {currentSection + 1}</span>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1B365D] mt-1.5 leading-tight">{sections[currentSection]?.title}</h2>
                  <div className={`w-12 h-0.5 ${colors.accent} mt-4 rounded-full`} />
                </div>
                <div className="learning-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{sections[currentSection]?.content || ''}</ReactMarkdown>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 px-3 sm:px-6 py-2.5 sm:py-3 bg-white">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => currentSection > 0 && setCurrentSection(currentSection - 1)} disabled={currentSection === 0} className="gap-1 sm:gap-2 rounded-full text-xs sm:text-sm" data-testid="reader-prev-btn">
            <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="hidden xs:inline">Previous</span><span className="xs:hidden">Prev</span>
          </Button>
          <div className="hidden sm:flex items-center gap-1.5">
            {sections.map((_, idx) => (
              <button key={idx} onClick={() => setCurrentSection(idx)}
                className={`w-2 h-2 rounded-full transition-all ${idx === currentSection ? 'bg-[#1B365D] scale-125' : idx < currentSection ? 'bg-emerald-400' : 'bg-slate-300'}`} />
            ))}
          </div>
          <span className="text-[10px] text-slate-400 sm:hidden">{currentSection + 1}/{totalSections}</span>
          <Button size="sm" onClick={handleNext} className="gap-1 sm:gap-2 bg-[#1B365D] hover:bg-[#1B365D]/90 rounded-full text-xs sm:text-sm" data-testid="reader-next-btn">
            {currentSection === totalSections - 1 ? (<>Complete <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></>) : (<>Next <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></>)}
          </Button>
        </div>
      </div>
    </div>
  );
};


// ─── Course View ───
const CourseView = ({ course, onBack, onOpenChapter, completedChapters, completedSections }) => {
  const theme = COURSE_THEME[course.color] || COURSE_THEME.blue;
  const chapters = course.chapters || [];
  const completedCount = chapters.filter(ch => completedChapters.includes(ch.id)).length;
  const overallProgress = chapters.length > 0 ? (completedCount / chapters.length) * 100 : 0;
  const totalTime = chapters.reduce((sum, ch) => sum + parseInt(ch.estimated_time), 0);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid={`course-view-${course.id}`}>
      <Card className={`border-0 shadow-sm overflow-hidden bg-gradient-to-br ${theme.bg}`}>
        <CardContent className="p-4 sm:p-6 md:p-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 sm:gap-6">
            <div>
              <Button variant="ghost" size="sm" onClick={onBack} className="text-white/70 hover:text-white hover:bg-white/10 gap-1.5 mb-3 -ml-2 px-2 text-xs sm:text-sm" data-testid="course-back-btn">
                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> All Courses
              </Button>
              <Badge className="bg-white/15 text-white border-0 mb-3 text-xs">
                <BookOpen className="w-3 h-3 mr-1" />{chapters.length} Chapters
              </Badge>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">{course.title}</h2>
              <p className="text-white/70 text-xs sm:text-sm md:text-base max-w-xl">{course.description}</p>
            </div>
            <div className="flex flex-row md:flex-col items-center md:items-end gap-2 min-w-[120px] sm:min-w-[160px]">
              <span className="text-3xl sm:text-4xl font-bold">{Math.round(overallProgress)}%</span>
              <p className="text-white/60 text-[10px] sm:text-xs">{completedCount}/{chapters.length} chapters &middot; ~{totalTime} min</p>
            </div>
          </div>
          <Progress value={overallProgress} className="h-1.5 sm:h-2 mt-4 sm:mt-5 bg-white/15" />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {[
          { label: 'Chapters', value: chapters.length, icon: BookOpen, color: 'text-blue-600 bg-blue-50' },
          { label: 'Completed', value: completedCount, icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Remaining', value: chapters.length - completedCount, icon: Clock, color: 'text-amber-600 bg-amber-50' },
          { label: 'Est. Time', value: `${totalTime}m`, icon: TrendingUp, color: 'text-purple-600 bg-purple-50' },
        ].map((stat) => (
          <Card key={stat.label} className="border-0 shadow-sm">
            <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className={`p-1.5 sm:p-2 rounded-lg ${stat.color}`}><stat.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" /></div>
              <div>
                <p className="text-[10px] sm:text-xs text-slate-500">{stat.label}</p>
                <p className="text-sm sm:text-lg font-bold text-[#1B365D]">{stat.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-2 sm:space-y-3">
        {chapters.map((chapter, idx) => {
          const colors = CHAPTER_COLORS[idx % CHAPTER_COLORS.length];
          const IconComp = ICON_MAP[chapter.icon] || BookOpen;
          const isCompleted = completedChapters.includes(chapter.id);
          const sectionsDone = chapter.sections.filter((_, si) => completedSections.includes(`${chapter.id}-${si}`)).length;
          const chapterProgress = chapter.sections.length > 0 ? (sectionsDone / chapter.sections.length) * 100 : 0;

          return (
            <motion.div key={chapter.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
              <Card
                className={`border-0 shadow-sm hover:shadow-md transition-all cursor-pointer group ${isCompleted ? 'ring-1 ring-emerald-200' : ''}`}
                onClick={() => onOpenChapter(chapter, idx)}
                data-testid={`learning-chapter-${chapter.id}`}
              >
                <CardContent className="p-0">
                  <div className="flex items-stretch">
                    <div className={`w-1 sm:w-1.5 rounded-l-xl ${isCompleted ? 'bg-emerald-400' : colors.accent}`} />
                    <div className="flex-1 p-3 sm:p-4 md:p-5 flex items-center gap-3 sm:gap-4">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-emerald-50' : colors.light}`}>
                        {isCompleted ? <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" /> : <IconComp className={`w-4 h-4 sm:w-5 sm:h-5 ${colors.text}`} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-widest ${isCompleted ? 'text-emerald-600' : colors.text}`}>
                            Ch. {chapter.order}
                          </span>
                          {isCompleted && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px] sm:text-[10px] py-0">Done</Badge>}
                        </div>
                        <h3 className="font-semibold text-[#1B365D] text-sm sm:text-base mb-0.5 truncate">{chapter.title}</h3>
                        <p className="text-xs sm:text-sm text-slate-500 truncate hidden sm:block">{chapter.subtitle}</p>
                        {!isCompleted && chapterProgress > 0 && (
                          <div className="mt-1.5 sm:mt-2 flex items-center gap-2">
                            <Progress value={chapterProgress} className="h-1 flex-1 max-w-[200px]" />
                            <span className="text-[10px] text-slate-400">{sectionsDone}/{chapter.sections.length}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        <div className="hidden md:flex flex-col items-end gap-1">
                          <Badge variant="outline" className="text-[10px]"><Clock className="w-3 h-3 mr-1" />{chapter.estimated_time}</Badge>
                          <span className="text-[10px] text-slate-400">{chapter.sections.length} section{chapter.sections.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors ${
                          isCompleted ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400 group-hover:bg-[#1B365D] group-hover:text-white'}`}>
                          {isCompleted ? <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};


// ─── Course Library with Search ───
const CourseLibrary = ({ courses, onSelectCourse, onSearchNavigate, completedChapters }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const totalChaptersAll = courses.reduce((s, c) => s + c.chapters.length, 0);
  const completedAll = courses.reduce((s, c) => s + c.chapters.filter(ch => completedChapters.includes(ch.id)).length, 0);
  const overallProgress = totalChaptersAll > 0 ? (completedAll / totalChaptersAll) * 100 : 0;

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    const results = [];
    const words = q.split(/\s+/);

    for (const course of courses) {
      for (const chapter of (course.chapters || [])) {
        for (let si = 0; si < (chapter.sections || []).length; si++) {
          const section = chapter.sections[si];
          const text = `${section.title} ${section.content}`.toLowerCase();
          const matches = words.every(w => text.includes(w));
          if (matches) {
            let snippet = '';
            const contentLower = section.content.toLowerCase();
            const firstWordIdx = contentLower.indexOf(words[0]);
            if (firstWordIdx >= 0) {
              const start = Math.max(0, firstWordIdx - 30);
              const end = Math.min(section.content.length, firstWordIdx + 80);
              let raw = section.content.substring(start, end).replace(/[#|*_>\-\[\]]/g, '').trim();
              if (start > 0) raw = '...' + raw;
              if (end < section.content.length) raw = raw + '...';
              for (const w of words) {
                const re = new RegExp(`(${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
                raw = raw.replace(re, '<mark class="bg-yellow-200 rounded px-0.5">$1</mark>');
              }
              snippet = raw;
            }
            results.push({
              courseId: course.id, courseTitle: course.title, courseColor: course.color,
              chapterId: chapter.id, chapterTitle: chapter.title, chapterOrder: chapter.order,
              sectionIndex: si, sectionTitle: section.title, snippet,
            });
          }
        }
      }
    }
    return results.slice(0, 20);
  }, [searchQuery, courses]);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="course-library">
      <Card className="border-0 shadow-sm overflow-hidden bg-gradient-to-br from-[#1B365D] via-[#1B365D] to-[#0071E3]">
        <CardContent className="p-4 sm:p-6 md:p-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 sm:gap-6">
            <div>
              <Badge className="bg-white/15 text-white border-0 mb-3 text-xs">
                <GraduationCap className="w-3 h-3 mr-1" />{courses.length} Courses
              </Badge>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2">Care Coordination Learning Paths</h2>
              <p className="text-white/70 text-xs sm:text-sm md:text-base max-w-xl">
                Master Enable Healthcare's full solution suite — EMR Sales, CCM, APCM, and RPM programs.
              </p>
            </div>
            <div className="flex flex-row md:flex-col items-center md:items-end gap-2 min-w-[120px] sm:min-w-[160px]">
              <span className="text-3xl sm:text-4xl font-bold">{Math.round(overallProgress)}%</span>
              <p className="text-white/60 text-[10px] sm:text-xs">{completedAll}/{totalChaptersAll} chapters</p>
            </div>
          </div>
          <Progress value={overallProgress} className="h-1.5 sm:h-2 mt-4 sm:mt-5 bg-white/15" />
        </CardContent>
      </Card>

      {/* Search Bar */}
      <div className="relative" data-testid="course-search">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search across all courses... (e.g. billing codes, RPM devices, APCM eligibility)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 h-10 sm:h-11 text-sm rounded-xl border-slate-200 focus:border-[#0071E3] focus:ring-[#0071E3]/20"
            data-testid="course-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100" data-testid="search-clear-btn">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
        <SearchResults
          results={searchResults}
          query={searchQuery}
          onClose={() => setSearchQuery('')}
          onSelect={(r) => {
            setSearchQuery('');
            onSearchNavigate(r);
          }}
        />
      </div>

      {/* Course Grid */}
      {!searchQuery && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
          {courses.map((course, idx) => {
            const theme = COURSE_THEME[course.color] || COURSE_THEME.blue;
            const CourseIcon = ICON_MAP[course.icon] || BookOpen;
            const chapters = course.chapters || [];
            const done = chapters.filter(ch => completedChapters.includes(ch.id)).length;
            const progress = chapters.length > 0 ? (done / chapters.length) * 100 : 0;
            const totalTime = chapters.reduce((s, ch) => s + parseInt(ch.estimated_time), 0);

            return (
              <motion.div key={course.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.06 }}>
                <Card
                  className={`border shadow-sm hover:shadow-lg transition-all cursor-pointer group h-full ${theme.card}`}
                  onClick={() => onSelectCourse(course)}
                  data-testid={`course-card-${course.id}`}
                >
                  <CardContent className="p-4 sm:p-5 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-3 sm:mb-4">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${theme.light}`}>
                        <CourseIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${theme.text}`} />
                      </div>
                      {done === chapters.length && chapters.length > 0 ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px] sm:text-[10px]">Complete</Badge>
                      ) : done > 0 ? (
                        <Badge className={`${theme.badge} border-0 text-[9px] sm:text-[10px]`}>{done}/{chapters.length}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] sm:text-[10px] text-slate-400">New</Badge>
                      )}
                    </div>

                    <h3 className="font-bold text-[#1B365D] text-sm sm:text-base mb-1 sm:mb-1.5 leading-snug">{course.title}</h3>
                    <p className="text-xs sm:text-sm text-slate-500 mb-3 sm:mb-4 flex-1 line-clamp-2">{course.description}</p>

                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-center justify-between text-[10px] sm:text-xs text-slate-400">
                        <span>{chapters.length} ch. &middot; ~{totalTime}m</span>
                        <span className="font-semibold text-[#1B365D]">{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-1 sm:h-1.5" />
                    </div>

                    <div className="mt-3 sm:mt-4 flex items-center justify-between">
                      <span className={`text-[10px] sm:text-xs font-semibold ${theme.text}`}>
                        {done === 0 ? 'Start Course' : done === chapters.length ? 'Review' : 'Continue'}
                      </span>
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors bg-slate-100 text-slate-400 group-hover:bg-[#1B365D] group-hover:text-white">
                        <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── Main Component ───
const LearningPaths = () => {
  const { getAuthHeaders } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCourse, setActiveCourse] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [activeChapterIndex, setActiveChapterIndex] = useState(0);
  const [completedChapters, setCompletedChapters] = useState(() => {
    const saved = localStorage.getItem('completedLearningChapters');
    return saved ? JSON.parse(saved) : [];
  });
  const [completedSections, setCompletedSections] = useState(() => {
    const saved = localStorage.getItem('completedLearningSections');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => { fetchCourses(); }, []);

  useEffect(() => {
    localStorage.setItem('completedLearningChapters', JSON.stringify(completedChapters));
  }, [completedChapters]);

  useEffect(() => {
    localStorage.setItem('completedLearningSections', JSON.stringify(completedSections));
  }, [completedSections]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/learning-paths`, getAuthHeaders());
      setCourses(response.data.courses || []);
    } catch (error) {
      toast.error('Failed to load learning paths');
    } finally {
      setLoading(false);
    }
  };

  const handleSectionComplete = (sectionKey) => {
    if (!completedSections.includes(sectionKey)) {
      setCompletedSections(prev => [...prev, sectionKey]);
    }
  };

  const handleChapterComplete = (chapterId) => {
    if (!completedChapters.includes(chapterId)) {
      setCompletedChapters(prev => [...prev, chapterId]);
      toast.success('Chapter completed! Great job!');
    }
    setActiveChapter(null);
  };

  const openChapter = (chapter, index) => {
    setActiveChapter(chapter);
    setActiveChapterIndex(index);
  };

  const handleSearchNavigate = useCallback((result) => {
    const course = courses.find(c => c.id === result.courseId);
    if (!course) return;
    setActiveCourse(course);
    const chapterIdx = course.chapters.findIndex(ch => ch.id === result.chapterId);
    if (chapterIdx >= 0) {
      const chapter = course.chapters[chapterIdx];
      setActiveChapter(chapter);
      setActiveChapterIndex(chapterIdx);
    }
  }, [courses]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-500">Loading learning paths...</div>
      </div>
    );
  }

  if (activeChapter) {
    return (
      <ChapterReader
        chapter={activeChapter}
        chapterIndex={activeChapterIndex}
        onClose={() => setActiveChapter(null)}
        onComplete={handleChapterComplete}
        completedSections={completedSections}
        onSectionComplete={handleSectionComplete}
      />
    );
  }

  if (activeCourse) {
    return (
      <CourseView
        course={activeCourse}
        onBack={() => setActiveCourse(null)}
        onOpenChapter={openChapter}
        completedChapters={completedChapters}
        completedSections={completedSections}
      />
    );
  }

  return (
    <CourseLibrary
      courses={courses}
      onSelectCourse={setActiveCourse}
      onSearchNavigate={handleSearchNavigate}
      completedChapters={completedChapters}
    />
  );
};

export default LearningPaths;
